import firebase from 'firebase/app';
import 'firebase/messaging';
import config from '../config';

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

let messaging = null;

function initMessaging() {
  if (!config.firebaseSettings || !config.firebaseSettings.apiKey) {
    return null;
  }
  if (!firebase.apps.length) {
    firebase.initializeApp(config.firebaseSettings);
  }
  if (firebase.messaging.isSupported && firebase.messaging.isSupported()) {
    messaging = firebase.messaging();
    return messaging;
  }
  return null;
}

messaging = initMessaging();

const STORAGE_LAST_POSTED_TOKEN = 'push-notify-last-posted-token';

function normalizeClickUrl(url) {
  if (!url || typeof url !== 'string') return null;
  let u = url.trim();
  if (u.indexOf('@') === 0) u = u.substring(1);
  if (u.indexOf('/') === 0) return window.location.origin + u;
  if (!/^https?:\/\//i.test(u)) return 'https://' + u;
  return u;
}

async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  return navigator.serviceWorker.ready;
}

export async function saveTokenToBackend(token) {
  if (!token) return;
  const last = localStorage.getItem(STORAGE_LAST_POSTED_TOKEN);
  if (last === token) return;
  const res = await fetch('/pushnotification/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pushToken: token }),
    credentials: 'same-origin'
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  localStorage.setItem(STORAGE_LAST_POSTED_TOKEN, token);
}

export const requestFCMToken = async () => {
  if (!messaging) {
    messaging = initMessaging();
  }
  if (!messaging) {
    throw new Error('Firebase messaging is not available');
  }
  if (!vapidKey) {
    throw new Error(
      'REACT_APP_FIREBASE_VAPID_KEY is missing. Rebuild the frontend with Firebase env vars set.'
    );
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permission not granted for notifications');
  }

  const serviceWorkerRegistration = await getServiceWorkerRegistration();
  const token = await messaging.getToken({
    vapidKey,
    serviceWorkerRegistration: serviceWorkerRegistration || undefined
  });
  if (!token) {
    throw new Error('Could not obtain FCM token');
  }
  return token;
};

export const getExistingFCMToken = async () => {
  if (!messaging) {
    messaging = initMessaging();
  }
  if (!messaging || !vapidKey) {
    return null;
  }
  if (Notification.permission !== 'granted') {
    return null;
  }
  try {
    const serviceWorkerRegistration = await getServiceWorkerRegistration();
    return await messaging.getToken({
      vapidKey,
      serviceWorkerRegistration: serviceWorkerRegistration || undefined
    });
  } catch (err) {
    console.warn('[push] getExistingFCMToken failed', err);
    return null;
  }
};

export const isNotificationSupported = () => {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    !!(firebase.messaging.isSupported && firebase.messaging.isSupported()) &&
    !!(config.firebaseSettings && config.firebaseSettings.apiKey) &&
    !!vapidKey
  );
};

export const getNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'unsupported';
};

/** Safari desktop / iOS WebKit — permission must be requested from a user gesture. */
export const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1 && ua.indexOf('crios') === -1;
};

export const isIOS = () => {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

/** Show in-app enable UI (bar) instead of auto requestPermission on load. */
export const needsExplicitPermissionUi = () => {
  return isSafari() || isIOS();
};

export const setupTokenRefresh = (onRefreshed) => {
  if (!messaging) return;
  messaging.onTokenRefresh(async () => {
    try {
      const serviceWorkerRegistration = await getServiceWorkerRegistration();
      const refreshed = await messaging.getToken({
        vapidKey,
        serviceWorkerRegistration: serviceWorkerRegistration || undefined
      });
      if (refreshed) {
        localStorage.removeItem(STORAGE_LAST_POSTED_TOKEN);
        await saveTokenToBackend(refreshed);
        if (onRefreshed) onRefreshed(refreshed);
      }
    } catch (err) {
      console.warn('[push] onTokenRefresh failed', err);
    }
  });
};

function stringifyDataPayload(raw) {
  const data = {};
  if (!raw) return data;
  Object.keys(raw).forEach((key) => {
    if (raw[key] != null) data[key] = String(raw[key]);
  });
  return data;
}

/** Foreground FCM messages while the app tab is open. */
export function setupForegroundMessageHandler() {
  if (!messaging) {
    messaging = initMessaging();
  }
  if (!messaging) return undefined;

  let active = true;

  messaging.onMessage((payload) => {
    if (!active) return;

    const notification = payload.notification || {};
    const data = stringifyDataPayload(payload.data || {});
    const title = notification.title || data.title || 'Notification';
    const body = notification.body || data.body || '';
    const clickUrl = data.url || data.link;

    if (Notification.permission !== 'granted') return;

    getServiceWorkerRegistration()
      .then((registration) => {
        if (!registration || !registration.showNotification) return;
        return registration.showNotification(title, {
          body,
          icon: data.icon || '/favicon.ico',
          badge: '/favicon.ico',
          tag: payload.messageId || data.id || title,
          data
        });
      })
      .catch((err) => {
        console.warn('[push] foreground showNotification failed', err);
        if (clickUrl) {
          const finalUrl = normalizeClickUrl(clickUrl);
          if (finalUrl) window.location.assign(finalUrl);
        }
      });
  });

  return () => {
    active = false;
  };
}

/** iOS/Safari fallback when SW cannot client.navigate — SW posts PUSH_NOTIFICATION_CLICK. */
export function listenForPushNotificationClickMessages() {
  if (!('serviceWorker' in navigator)) return undefined;

  const handler = (event) => {
    const msg = event.data;
    if (!msg || msg.type !== 'PUSH_NOTIFICATION_CLICK') return;

    const finalUrl = msg.finalUrl || normalizeClickUrl(msg.url);
    if (finalUrl) {
      window.location.assign(finalUrl);
      return;
    }
    if (msg.url && msg.url.indexOf('/') === 0) {
      window.location.assign(window.location.origin + msg.url);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

export { messaging };
